import { TableHeading } from '../../../widgets/table';
import React from 'react';
import { PaletteType } from 'src/shared/enums';
import { SmallColContainer } from 'src/shared/globals';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';

export const ColorColContainer = styled.div`
    display: flex;
    justify-content: space-evenly;
    align-items: center;
    width: 100%;
`;

// Define all possible column types
export const COLUMN_TYPES = {
    PANTONE_CODE: 'pantoneCode',
    NAME: 'name',
    ARROW: 'arrow',
    COLOR_PICKER: 'colorPicker',
    RGB: 'rgb',
    CMYK: 'cmyk',
    HEX: 'hex',
    ACTIONS: 'actions'
} as const;

export type ColumnType = (typeof COLUMN_TYPES)[keyof typeof COLUMN_TYPES];

// Define all possible columns with their headings and widths
export const ALL_COLUMNS: Record<ColumnType, TableHeading> = {
    [COLUMN_TYPES.PANTONE_CODE]: [<SmallColContainer>{T._('Pantone code', 'Admin')}</SmallColContainer>, '1.52fr'],
    [COLUMN_TYPES.NAME]: [<SmallColContainer>{T._('Name', 'Admin')}</SmallColContainer>, '1.50fr'],
    [COLUMN_TYPES.ARROW]: [<SmallColContainer></SmallColContainer>, '40px'],
    [COLUMN_TYPES.COLOR_PICKER]: [<ColorColContainer></ColorColContainer>, '60px'],
    [COLUMN_TYPES.RGB]: [<SmallColContainer>{T._('RGB', 'Admin')}</SmallColContainer>, '0.79fr'],
    [COLUMN_TYPES.CMYK]: [<SmallColContainer>{T._('CMYK', 'Admin')}</SmallColContainer>, '0.79fr'],
    [COLUMN_TYPES.HEX]: [<SmallColContainer>{T._('Hex', 'Admin')}</SmallColContainer>, '0.70fr'],
    [COLUMN_TYPES.ACTIONS]: [<SmallColContainer></SmallColContainer>, '110px']
};

// Define column orders for different palette types
export const COLUMN_ORDERS: Record<PaletteType, ColumnType[]> = {
    [PaletteType.Pantone]: [
        COLUMN_TYPES.PANTONE_CODE,
        COLUMN_TYPES.NAME,
        COLUMN_TYPES.ARROW,
        COLUMN_TYPES.COLOR_PICKER,
        COLUMN_TYPES.RGB,
        COLUMN_TYPES.HEX,
        COLUMN_TYPES.ACTIONS
    ],
    [PaletteType.CMYK]: [
        COLUMN_TYPES.CMYK,
        COLUMN_TYPES.NAME,
        COLUMN_TYPES.ARROW,
        COLUMN_TYPES.COLOR_PICKER,
        COLUMN_TYPES.RGB,
        COLUMN_TYPES.HEX,
        COLUMN_TYPES.ACTIONS
    ],
    [PaletteType.None]: [
        COLUMN_TYPES.PANTONE_CODE,
        COLUMN_TYPES.NAME,
        COLUMN_TYPES.ARROW,
        COLUMN_TYPES.COLOR_PICKER,
        COLUMN_TYPES.RGB,
        COLUMN_TYPES.CMYK,
        COLUMN_TYPES.HEX,
        COLUMN_TYPES.ACTIONS
    ]
};

export const getPaletteColorsHeadings = (paletteType: PaletteType, isZakekeDefault: boolean): TableHeading[] => {
    const columnOrder = COLUMN_ORDERS[paletteType] || COLUMN_ORDERS[PaletteType.None];

    let filteredColumns = columnOrder;

    // Filter out actions column for Zakeke default palettes
    if (isZakekeDefault) {
        filteredColumns = columnOrder.filter((col) => col !== COLUMN_TYPES.ACTIONS);
    }

    return filteredColumns.map((columnType) => ALL_COLUMNS[columnType]);
};

// Export function to get column order for use in table cells
export const getColumnOrder = (paletteType: PaletteType, isZakekeDefault: boolean): ColumnType[] => {
    const columnOrder = COLUMN_ORDERS[paletteType] || COLUMN_ORDERS[PaletteType.None];

    if (isZakekeDefault) {
        return columnOrder.filter((col) => col !== COLUMN_TYPES.ACTIONS);
    }

    return columnOrder;
};

// Legacy export for backward compatibility
export const paletteColorsHeadings = [
    ALL_COLUMNS[COLUMN_TYPES.PANTONE_CODE],
    ALL_COLUMNS[COLUMN_TYPES.NAME],
    ALL_COLUMNS[COLUMN_TYPES.ARROW],
    ALL_COLUMNS[COLUMN_TYPES.COLOR_PICKER],
    ALL_COLUMNS[COLUMN_TYPES.RGB],
    ALL_COLUMNS[COLUMN_TYPES.CMYK],
    ALL_COLUMNS[COLUMN_TYPES.HEX],
    ALL_COLUMNS[COLUMN_TYPES.ACTIONS]
] as TableHeading[];
